<template>
  <v-dialog
    max-width="600"
    class="template-pop-up template-pop-up-fix"
    v-model="assignChannelDialog">
    <AssignChannel
      :frameNo="frameNo"
      v-on:close="assignChannelDialog = false" />
  </v-dialog>
  <div
    class="outer-container"
    :class="{
      'cued-border': cuedScene,
      'single-frame':
        !disablePositionalBorderRadius && framePosition === 'single',
      'first-frame':
        !disablePositionalBorderRadius && framePosition === 'first',
      'middle-frame':
        !disablePositionalBorderRadius && framePosition === 'middle',
      'last-frame': !disablePositionalBorderRadius && framePosition === 'last',
      'uniform-border': disablePositionalBorderRadius,
      'no-interactivity': props.disableInteractivity,
    }"
    @click="handleClick"
    :title="title">
    <div class="inner-container">
      <v-fade-transition>
        <DropZone
          v-if="previewFeatureEnabled && state.cueTakeUI.sceneDragActive" />

        <div
          v-else
          class="dropped-or-default">
          <CuedScene :frameNo="frameNo" />
        </div>
      </v-fade-transition>
    </div>
    <div
      class="loading-overlay"
      v-if="deluge?.temp_stat?.loading">
      <div class="loading-container">
        <v-progress-circular
          indeterminate
          color="primary"
          class="loading-spinner"
          :size="25"></v-progress-circular>
        <div class="loading-text">
          <template v-if="loadingSceneDisplayName">
            Loading "{{ loadingSceneDisplayName }}"...
          </template>
          <template v-else>Loading...</template>
        </div>
      </div>
    </div>
    <div v-if="!cuedScene && !state.cueTakeUI.sceneDragActive">
      <div
        v-if="deluge.frames[props.frameNo] && !deluge.temp_stat?.loading"
        style="
          top: 0;
          position: absolute;
          display: flex;
          width: 100%;
          padding: 8px;
        ">
        <div
          style="
            border-radius: 5px;
            color: white;
            border-color: rgba(255, 255, 255, 0.08);
            border-style: solid;
            border-width: 1px;
            display: flex;
          "
          :style="'background: ' + deluge.frames[props.frameNo].color"
          v-if="deluge.items[deluge.frames[props.frameNo].did]"
          class="ma-3 pa-2"
          @click="assignChannelDialog = true">
          <v-icon class="mr-2">
            {{ 'mdi-numeric-' + (deluge.frames[props.frameNo]?.number || props.frameNo) + '-box' }}
          </v-icon>
          <div v-if="deluge.frames[props.frameNo].nickname">
            {{ deluge.frames[props.frameNo].nickname }}
          </div>
          <div v-else-if="deluge.frames[props.frameNo].frame_id">
            {{ deluge.frames[props.frameNo].frame_id }}
          </div>
          <div v-else>Frame</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { state } from '../../../stores/deluge';
import { deluge } from '@/stores/deluge.js';

import AssignChannel from '../AssignChannel.vue';

import CuedScene from '../CueTake/CuedScene.vue';
import DropZone from '../CueTake/DropZone.vue';

const props = defineProps({
  frameNo: {
    type: Number,
    required: true,
  },
  disablePositionalBorderRadius: {
    type: Boolean,
    default: false,
  },
  disableInteractivity: {
    type: Boolean,
    default: false,
  },
});

const assignChannelDialog = ref(false);

function handleClick() {
  // respect the disableInteractivity prop: don't open dialog or respond to clicks
  if (props.disableInteractivity) return;
  assignChannelDialog.value = true;
}

function GetLogo(scene_name) {
  for (const template in deluge.templates) {
    if (template == scene_name) {
      return deluge.templates[template].icon;
    }
  }
  return 'mdi-cube';
}

const previewFeatureEnabled = computed(() => {
  return true;
});

const channelAssigned = computed(() => {
  if (!deluge.frames) return false;
  if (!deluge.frames?.[props.frameNo]) return false;
  const frame = deluge.frames[props.frameNo];
  if (!frame || !frame.did) return false;
  return true;
});

const title = computed(() => {
  return channelAssigned.value
    ? `Swap Channel in Frame ${props.frameNo}`
    : `Assign Channel to Frame ${props.frameNo}`;
});

const cuedScene = computed(() => {
  if (!deluge.new_cued_dids) return false;
  const cuedDid = deluge.new_cued_dids?.[props.frameNo];
  if (!cuedDid) return null;
  const scene = deluge.items[cuedDid];
  return scene;
  return null;
});

const loadingSceneDisplayName = computed(() => {
  // Backend now sends display_name directly in temp_stat.scene
  return deluge.temp_stat?.scene || null;
});

// Computed property to determine frame position
const framePosition = computed(() => {
  const framesInUse = getFramesInUse();
  const totalFrames = framesInUse.length;
  const currentFrameIndex = framesInUse.indexOf(props.frameNo);

  if (totalFrames === 1) {
    return 'single';
  } else if (currentFrameIndex === 0) {
    return 'first';
  } else if (currentFrameIndex === totalFrames - 1) {
    return 'last';
  } else {
    return 'middle';
  }
});

// You'll need to implement this function or import it from your store
function getFramesInUse() {
  // return only frames that have channels assigned (are actually being used)
  if (!deluge.frames) return [];

  return Object.keys(deluge.frames)
    .map(Number)
    .filter((frameNo) => {
      const frame = deluge.frames[frameNo];
      return frame && frame.did; // Only include frames that have a channel assigned (did exists)
    })
    .sort((a, b) => a - b);
}
</script>

<style scoped>
.outer-container {
  padding: 8px;
  flex: 1;
  background: var(--v-cardColor-base);
  width: 100%;
  height: 232px;
  position: relative;
  user-select: none;
}

.outer-container:hover {
  background: var(--primary-color);
  cursor: pointer;
}

/* When interactivity is disabled: remove hover effects and prevent clicks */
.outer-container.no-interactivity {
  pointer-events: none;
}

.outer-container.no-interactivity:hover {
  /* keep the original base background and default cursor */
  background: var(--v-cardColor-base);
  cursor: default;
}

/*Controlling border radius based on frame position*/
.outer-container.uniform-border {
  border-radius: 6px;
}

.outer-container.single-frame {
  border-radius: 6px;
}

.outer-container.first-frame {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
}

.outer-container.middle-frame {
  border-radius: 0px;
}

.outer-container.last-frame {
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.cued-border {
  background-color: v-bind('deluge.frames[frameNo].color');
}

.inner-container {
  align-items: center;
  justify-content: center;
  background: #0d0d0d;
  border-radius: 4px;
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  margin-right: auto;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  left: 0;
  right: 0;
}

.dropped-or-default {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
}

/* iPad portrait mode - reduce preview height */
@media only screen and (min-width: 551px) and (max-width: 850px) {
  .outer-container {
    height: 180px;
  }
}

@media only screen and (max-width: 550px) {
  .outer-container {
    max-height: 150px;
  }
}

.loading-overlay {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  justify-content: center;
  overflow: hidden;
}

.loading-container {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border-radius: 5px;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #141414eb;
  padding: 8px 12px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.loading-spinner {
  flex-shrink: 0;
}

.loading-text {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.4;
  min-width: 0;
}
</style>
